import React, { useMemo, useState } from "react";
import {
  TrendingUp,
  User,
  Calendar,
  Target,
  Award,
  Clock,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";
import Wrapper from "../../../components/Wrapper";

const MOCK_WORKERS = [
  { id: "w1", name: "Shruti", role: "packer" },
  { id: "w2", name: "Roshni", role: "qa" },
  { id: "w3", name: "Aman", role: "manager" },
];

const MOCK_REAGENTS = {
  r1: { name: "NO3 Reagent" },
  r2: { name: "NPK Extraction" },
  r3: { name: "P Reagent B" },
};

/** Seeded mock rows for w1 — enough to exercise UI like Lab_Management */
const MOCK_LOGS_W1 = [
  { id: "l1", worker_id: "w1", work_date: "2026-04-08", quantity_completed: 45, reagent_id: "r1" },
  { id: "l2", worker_id: "w1", work_date: "2026-04-08", quantity_completed: 30, reagent_id: "r2" },
  { id: "l3", worker_id: "w1", work_date: "2026-04-07", quantity_completed: 80, reagent_id: "r1" },
  { id: "l4", worker_id: "w1", work_date: "2026-04-06", quantity_completed: 60, reagent_id: "r3" },
];

const MOCK_TARGETS_W1 = [
  { id: "t1", worker_id: "w1", target_date: "2026-04-08", target_quantity: 80 },
  { id: "t2", worker_id: "w1", target_date: "2026-04-07", target_quantity: 100 },
  { id: "t3", worker_id: "w1", target_date: "2026-04-06", target_quantity: 50 },
];

const MOCK_ATTENDANCE_W1 = [
  { work_date: "2026-04-08", status: "present", check_in_time: "09:05:00", check_out_time: "17:10:00" },
  { work_date: "2026-04-07", status: "present", check_in_time: "09:20:00", check_out_time: "17:00:00" },
  { work_date: "2026-04-06", status: "absent", check_in_time: null, check_out_time: null },
];

const MOCK_SUMMARIES_W1 = [
  { summary_date: "2026-04-08", challenges: "Minor equipment delay in morning", verified_by: "mgr-1" },
  { summary_date: "2026-04-07", challenges: null, verified_by: null },
];

function getRoleLabel(role) {
  switch (role) {
    case "packer":
      return "Lab Tech";
    case "qa":
      return "QA Specialist";
    case "manager":
      return "Lead";
    default:
      return role;
  }
}

function formatDateCompact(dateStr) {
  return new Date(dateStr).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function buildPerformanceForWorker(worker, dateRange, workLogs, targets, attendance, summaries) {
  const inRange = (d) => d >= dateRange.start && d <= dateRange.end;
  const logs = workLogs.filter((l) => l.worker_id === worker.id && inRange(l.work_date));
  const targs = targets.filter((t) => t.worker_id === worker.id && inRange(t.target_date));
  const att = attendance.filter((a) => a.worker_id === worker.id && inRange(a.work_date));
  const sums = summaries.filter((s) => s.worker_id === worker.id && inRange(s.summary_date));

  const totalUnits = logs.reduce((sum, log) => sum + log.quantity_completed, 0);
  const uniqueDays = new Set(logs.map((log) => log.work_date)).size;
  const avgDailyUnits = uniqueDays > 0 ? Math.round(totalUnits / uniqueDays) : 0;

  const targetsByDate = new Map();
  targs.forEach((t) => {
    targetsByDate.set(t.target_date, (targetsByDate.get(t.target_date) || 0) + t.target_quantity);
  });
  const workByDate = new Map();
  logs.forEach((log) => {
    workByDate.set(log.work_date, (workByDate.get(log.work_date) || 0) + log.quantity_completed);
  });

  let achievedTargets = 0;
  let totalTargets = 0;
  targetsByDate.forEach((targetQty, date) => {
    totalTargets++;
    const completed = workByDate.get(date) || 0;
    if (completed >= targetQty) achievedTargets++;
  });
  const targetAchievementRate = totalTargets > 0 ? Math.round((achievedTargets / totalTargets) * 100) : 0;

  const daysPresent = att.filter((a) => a.status === "present").length;
  const daysAbsent = att.filter((a) => a.status === "absent").length;
  const onTimeCount = att.filter((a) => {
    if (!a.check_in_time) return false;
    const [hours] = a.check_in_time.split(":").map(Number);
    return hours < 10;
  }).length;
  const onTimeRate = att.length > 0 ? Math.round((onTimeCount / att.length) * 100) : 0;

  const summariesSubmitted = sums.length;
  const summariesVerified = sums.filter((s) => s.verified_by).length;

  const allDates = new Set();
  logs.forEach((log) => allDates.add(log.work_date));
  att.forEach((a) => allDates.add(a.work_date));
  targs.forEach((t) => allDates.add(t.target_date));

  const dayDetails = Array.from(allDates)
    .sort((a, b) => b.localeCompare(a))
    .map((date) => {
      const dayLogs = logs
        .filter((log) => log.work_date === date)
        .map((log) => ({ ...log, reagent: MOCK_REAGENTS[log.reagent_id] || { name: "Reagent" } }));
      const dayTarget = targs.find((t) => t.target_date === date) || null;
      const dayAttendance = att.find((a) => a.work_date === date) || null;
      const daySummary = sums.find((s) => s.summary_date === date) || null;
      const totalCompleted = dayLogs.reduce((sum, log) => sum + log.quantity_completed, 0);
      const targetQuantity = dayTarget?.target_quantity || 0;
      const achievementRate = targetQuantity > 0 ? Math.round((totalCompleted / targetQuantity) * 100) : 0;
      return {
        date,
        workLogs: dayLogs,
        target: dayTarget,
        attendance: dayAttendance,
        summary: daySummary,
        totalCompleted,
        targetQuantity,
        achievementRate,
      };
    });

  return {
    performance: {
      worker,
      totalUnits,
      totalDays: uniqueDays,
      avgDailyUnits,
      targetAchievementRate,
      daysPresent,
      daysAbsent,
      onTimeRate,
      summariesSubmitted,
      summariesVerified,
    },
    dayDetails,
  };
}

export default function PerformancePage() {
  const [workers] = useState(MOCK_WORKERS);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return { start: start.toISOString().split("T")[0], end: end.toISOString().split("T")[0] };
  });

  const mockLogs = useMemo(() => {
    const w2 = [
      { id: "l5", worker_id: "w2", work_date: "2026-04-08", quantity_completed: 40, reagent_id: "r2" },
      { id: "l6", worker_id: "w2", work_date: "2026-04-07", quantity_completed: 50, reagent_id: "r1" },
    ];
    const w3 = [{ id: "l7", worker_id: "w3", work_date: "2026-04-08", quantity_completed: 25, reagent_id: "r3" }];
    const attachReagent = (logs) =>
      logs.map((l) => ({ ...l, reagent: MOCK_REAGENTS[l.reagent_id] || { name: "Reagent" } }));
    return [...MOCK_LOGS_W1, ...w2, ...w3].map((l) => ({ ...l, reagent: MOCK_REAGENTS[l.reagent_id] }));
  }, []);

  const mockTargets = useMemo(
    () => [
      ...MOCK_TARGETS_W1,
      { id: "t4", worker_id: "w2", target_date: "2026-04-08", target_quantity: 50 },
      { id: "t5", worker_id: "w2", target_date: "2026-04-07", target_quantity: 60 },
      { id: "t6", worker_id: "w3", target_date: "2026-04-08", target_quantity: 40 },
    ],
    []
  );

  const mockAttendance = useMemo(
    () => [
      ...MOCK_ATTENDANCE_W1.map((a, i) => ({ ...a, worker_id: "w1", id: `a-w1-${i}` })),
      { worker_id: "w2", work_date: "2026-04-08", status: "present", check_in_time: "09:30:00", check_out_time: "17:00:00" },
      { worker_id: "w2", work_date: "2026-04-07", status: "present", check_in_time: "08:55:00", check_out_time: "17:05:00" },
      { worker_id: "w3", work_date: "2026-04-08", status: "present", check_in_time: "10:15:00", check_out_time: "18:00:00" },
    ],
    []
  );

  const mockSummaries = useMemo(
    () => [
      ...MOCK_SUMMARIES_W1.map((s, i) => ({ ...s, worker_id: "w1", id: `s-w1-${i}` })),
      { worker_id: "w2", summary_date: "2026-04-08", challenges: null, verified_by: "mgr-1" },
    ],
    []
  );

  const { performance, dayDetails } = useMemo(() => {
    if (!selectedWorker) return { performance: null, dayDetails: [] };
    const logs = mockLogs.map(({ reagent, ...l }) => l);
    return buildPerformanceForWorker(selectedWorker, dateRange, logs, mockTargets, mockAttendance, mockSummaries);
  }, [selectedWorker, dateRange, mockLogs, mockTargets, mockAttendance, mockSummaries]);

  const shiftDateRange = (days) => {
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    start.setDate(start.getDate() + days);
    end.setDate(end.getDate() + days);
    setDateRange({ start: start.toISOString().split("T")[0], end: end.toISOString().split("T")[0] });
  };

  if (!selectedWorker) {
    return (
      <Wrapper>
        <div className="space-y-4 pb-20 sm:space-y-6 lg:pb-6">
          <div className="w-full px-1 text-left">
            <h2 className="m-0 text-xl font-bold text-slate-800 text-left sm:text-2xl">Individual Performance</h2>
            <p className="mt-1 text-sm text-slate-500 text-left sm:text-base">Monitor each crew member&apos;s metrics</p>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
            {workers.map((worker) => (
              <button
                key={worker.id}
                type="button"
                onClick={() => setSelectedWorker(worker)}
                className="rounded-xl border border-slate-200 bg-white p-4 text-left transition-all hover:border-emerald-300 hover:shadow-lg active:scale-[0.98] sm:p-6"
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 text-base font-bold text-white sm:h-14 sm:w-14 sm:text-xl">
                    {worker.name.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <h3 className="truncate text-sm font-semibold text-slate-800 sm:text-lg">{worker.name}</h3>
                    <p className="text-xs text-slate-500 sm:text-sm">{getRoleLabel(worker.role)}</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-emerald-600 sm:mt-4">
                  <BarChart3 size={14} />
                  <span className="text-xs sm:text-sm">View Performance</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="space-y-4 pb-20 sm:space-y-6 lg:pb-6">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2 sm:gap-4">
            <button type="button" onClick={() => setSelectedWorker(null)} className="shrink-0 rounded-lg p-2 hover:bg-slate-100">
              <ChevronLeft size={20} />
            </button>
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 text-base font-bold text-white sm:h-12 sm:w-12 sm:text-lg">
                {selectedWorker.name.charAt(0)}
              </div>
              <div className="min-w-0">
                <h2 className="truncate text-lg font-bold text-slate-800 sm:text-2xl">{selectedWorker.name}</h2>
                <p className="text-xs text-slate-500 sm:text-sm">{getRoleLabel(selectedWorker.role)}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 self-end sm:self-auto sm:gap-2">
            <button type="button" onClick={() => shiftDateRange(-30)} className="rounded-lg p-1.5 hover:bg-slate-100 sm:p-2">
              <ChevronLeft size={18} />
            </button>
            <div className="whitespace-nowrap rounded-lg bg-slate-100 px-2 py-1.5 text-xs sm:px-4 sm:py-2 sm:text-sm">
              <span className="hidden sm:inline">
                {dateRange.start} to {dateRange.end}
              </span>
              <span className="sm:hidden">
                {formatDateCompact(dateRange.start)} - {formatDateCompact(dateRange.end)}
              </span>
            </div>
            <button type="button" onClick={() => shiftDateRange(30)} className="rounded-lg p-1.5 hover:bg-slate-100 sm:p-2">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {performance ? (
          <>
            <div className="grid grid-cols-2 gap-2 sm:gap-4 lg:grid-cols-4">
              <div className="rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 p-3 text-white sm:p-4">
                <div className="mb-1 flex items-center gap-2 sm:mb-2">
                  <TrendingUp size={16} className="sm:hidden" />
                  <TrendingUp size={20} className="hidden sm:block" />
                  <span className="text-xs text-emerald-100 sm:text-sm">Total Output</span>
                </div>
                <p className="text-xl font-bold sm:text-3xl">{performance.totalUnits.toLocaleString()}</p>
                <p className="text-xs text-emerald-100 sm:text-sm">{performance.totalDays} days</p>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
                <div className="mb-1 flex items-center gap-2 sm:mb-2">
                  <Target className="text-blue-600" size={16} />
                  <span className="truncate text-xs text-slate-500 sm:text-sm">Target</span>
                </div>
                <p className="text-xl font-bold text-slate-800 sm:text-3xl">{performance.targetAchievementRate}%</p>
                <p className="text-xs text-slate-500 sm:text-sm">achieved</p>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
                <div className="mb-1 flex items-center gap-2 sm:mb-2">
                  <Calendar className="text-amber-600" size={16} />
                  <span className="truncate text-xs text-slate-500 sm:text-sm">Attendance</span>
                </div>
                <p className="text-xl font-bold text-slate-800 sm:text-3xl">{performance.daysPresent}</p>
                <p className="text-xs text-slate-500 sm:text-sm">{performance.daysAbsent} absent</p>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
                <div className="mb-1 flex items-center gap-2 sm:mb-2">
                  <Clock className="text-teal-600" size={16} />
                  <span className="truncate text-xs text-slate-500 sm:text-sm">Punctuality</span>
                </div>
                <p className="text-xl font-bold text-slate-800 sm:text-3xl">{performance.onTimeRate}%</p>
                <p className="text-xs text-slate-500 sm:text-sm">on-time</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="mb-3 flex items-center gap-2 sm:mb-4">
                  <Award className="text-emerald-600" size={18} />
                  <h3 className="text-sm font-semibold text-slate-800 sm:text-base">Productivity Score</h3>
                </div>
                <div className="relative flex h-28 items-center justify-center sm:h-32">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg className="h-24 w-24 -rotate-90 transform sm:h-32 sm:w-32">
                      <circle cx="50%" cy="50%" r="40%" stroke="#e2e8f0" strokeWidth="12" fill="none" />
                      <circle
                        cx="50%"
                        cy="50%"
                        r="40%"
                        stroke="#10b981"
                        strokeWidth="12"
                        fill="none"
                        strokeDasharray={`${(performance.targetAchievementRate / 100) * 251} 251`}
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-slate-800 sm:text-3xl">{performance.targetAchievementRate}%</p>
                    <p className="text-xs text-slate-500">Achievement</p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="mb-3 flex items-center gap-2 sm:mb-4">
                  <BarChart3 className="text-blue-600" size={18} />
                  <h3 className="text-sm font-semibold text-slate-800 sm:text-base">Daily Average</h3>
                </div>
                <p className="mb-2 text-3xl font-bold text-slate-800 sm:text-4xl">{performance.avgDailyUnits.toLocaleString()}</p>
                <p className="text-xs text-slate-500 sm:text-sm">units per working day</p>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100 sm:mt-4">
                  <div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${Math.min((performance.avgDailyUnits / 300) * 100, 100)}%` }} />
                </div>
                <p className="mt-1 text-xs text-slate-400">Benchmark: 300 units/day</p>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4 sm:col-span-2 lg:col-span-1">
                <div className="mb-3 flex items-center gap-2 sm:mb-4">
                  <User className="text-teal-600" size={18} />
                  <h3 className="text-sm font-semibold text-slate-800 sm:text-base">Compliance</h3>
                </div>
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-600 sm:text-sm">Summaries Submitted</span>
                    <span className="text-sm font-medium text-slate-800 sm:text-base">{performance.summariesSubmitted}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-600 sm:text-sm">Summaries Verified</span>
                    <span className="text-sm font-medium text-slate-800 sm:text-base">{performance.summariesVerified}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-600 sm:text-sm">Verification Rate</span>
                    <span className="text-sm font-medium text-emerald-600 sm:text-base">
                      {performance.summariesSubmitted > 0
                        ? Math.round((performance.summariesVerified / performance.summariesSubmitted) * 100)
                        : 0}
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <div className="border-b border-slate-200 p-3 sm:p-4">
                <h3 className="text-sm font-semibold text-slate-800 sm:text-base">Daily Breakdown</h3>
                <p className="text-xs text-slate-500 sm:text-sm">Detailed view of each working day</p>
              </div>
              <div className="divide-y divide-slate-100">
                {dayDetails.length === 0 ? (
                  <div className="p-6 text-center text-slate-500">No activity in this date range</div>
                ) : (
                  dayDetails.map((day) => (
                    <div key={day.date} className="p-3 transition-colors hover:bg-slate-50 sm:p-4">
                      <div className="mb-2 flex flex-col items-start justify-between gap-2 sm:mb-3 sm:flex-row sm:items-center">
                        <div className="flex min-w-0 items-start gap-2 sm:items-center sm:gap-3">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 sm:h-10 sm:w-10">
                            <Calendar className="text-slate-500" size={16} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800 sm:text-base">{day.date}</p>
                            <div className="mt-0.5 flex flex-wrap items-center gap-1 sm:mt-1 sm:gap-2">
                              {day.attendance?.status === "present" ? (
                                <span className="flex items-center gap-1 text-xs text-emerald-600">
                                  <CheckCircle size={12} /> Present
                                </span>
                              ) : day.attendance?.status === "absent" ? (
                                <span className="flex items-center gap-1 text-xs text-red-600">
                                  <XCircle size={12} /> Absent
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-xs text-slate-400">
                                  <AlertCircle size={12} /> No record
                                </span>
                              )}
                              {day.attendance?.check_in_time ? (
                                <span className="hidden text-xs text-slate-500 sm:inline">
                                  {day.attendance.check_in_time} - {day.attendance.check_out_time || "ongoing"}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="flex items-center gap-1 sm:gap-2">
                            <span className={`text-base font-bold sm:text-lg ${day.achievementRate >= 100 ? "text-emerald-600" : "text-slate-800"}`}>
                              {day.totalCompleted}
                            </span>
                            {day.targetQuantity > 0 ? <span className="text-xs text-slate-400 sm:text-sm">/ {day.targetQuantity}</span> : null}
                          </div>
                          {day.targetQuantity > 0 ? (
                            <div className="mt-1 h-1.5 w-16 overflow-hidden rounded-full bg-slate-100 sm:w-24">
                              <div
                                className={`h-full transition-all duration-300 ${day.achievementRate >= 100 ? "bg-emerald-500" : "bg-blue-500"}`}
                                style={{ width: `${Math.min(day.achievementRate, 100)}%` }}
                              />
                            </div>
                          ) : null}
                        </div>
                      </div>
                      {day.workLogs.length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-1.5 sm:gap-2">
                          {day.workLogs.map((log) => (
                            <span key={log.id} className="max-w-[140px] truncate rounded bg-slate-100 px-2 py-1 text-xs text-slate-600 sm:max-w-none">
                              {log.reagent?.name}: {log.quantity_completed}
                            </span>
                          ))}
                        </div>
                      ) : null}
                      {day.summary?.challenges ? (
                        <p className="mt-2 flex items-start gap-1 text-xs text-amber-600 sm:text-sm">
                          <AlertCircle size={14} className="mt-0.5 shrink-0" />
                          <span className="line-clamp-2">{day.summary.challenges}</span>
                        </p>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </Wrapper>
  );
}
